import { lazy, Suspense, useEffect } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useRoutes,
} from 'react-router-dom';
import { CashRoutes } from 'routes/cash/CashRoutes';
import { CloseRoutes } from 'routes/close/CloseRoutes';
import { consolidationRoutes } from 'routes/consolidation/ConsolidationRoutes';
import { IntegrationsRoutes } from 'routes/integrations/IntegrationsRoutes';
import { MappingRoutes } from 'routes/mapping/MappingRoutes';
import { reportsHubRoutes } from 'routes/reports/ReportsHubRoutes';
import { RevenueRoutes } from 'routes/revenue/RevenueRoutes';
import { getSettingsRoutes } from 'routes/settings/SettingsRoutes';
import { useGeneralRoutes } from 'routes/useGeneralRoutes';

import { ModuleSectionTitle } from 'components/ModuleSectionTitle';
import { PermissionGuard } from 'components/PermissionGuard';
import { LoadingAnimation } from 'components/loading/LoadingAnimation';

import { useMenu } from 'hooks/utils/useMenu';

import { featureFlags } from 'utils/config/featureFlags';

import { useAuthStore } from 'store/index';

// App Layout - Keep this eager since it's needed immediately
import { AppLayout } from 'layout/AppLayout';

// Lazy load all page components
const NotFound = lazy(() =>
  import('pages/NotFound').then((m) => ({ default: m.NotFound }))
);

const ApAndAccurals = lazy(
  () => import('pages/subledgers/ap-and-accurals/demo/ApAndAccurals')
);

const DemoBillDetails = lazy(() =>
  import('pages/subledgers/ap-and-accurals/demo/DemoBillDetails').then((m) => ({
    default: m.DemoBillDetails,
  }))
);

const FixedAssets = lazy(() =>
  import('pages/subledgers/FixedAssets/FixedAssets').then((m) => ({
    default: m.FixedAssets,
  }))
);

const Prepaids = lazy(() =>
  import('pages/subledgers/Prepaids').then((m) => ({ default: m.Prepaids }))
);

const ExpensesAndAccountsPayable = lazy(() =>
  import('pages/subledgers/expenses-and-accounts-payable/ExpensesAndAccountsPayable').then(
    (m) => ({ default: m.ExpensesAndAccountsPayable })
  )
);

const BillDetails = lazy(() =>
  import('pages/subledgers/expenses-and-accounts-payable/bill-analysis/BillDetails').then(
    (m) => ({ default: m.BillDetails })
  )
);

const FluxAnalysisDetails = lazy(() =>
  import('pages/period-close/flux-analysis/WrapperFluxAnalysis').then((m) => ({
    default: m.WrapperFluxAnalysis,
  }))
);

const FluxAnalysisList = lazy(() =>
  import('pages/period-close/flux-analysis/FluxAnalysisList').then((m) => ({
    default: m.FluxAnalysisList,
  }))
);

const IntercompanyEliminations = lazy(() =>
  import('pages/reporting/intercompany-eliminations/IntercompanyEliminations').then(
    (m) => ({ default: m.IntercompanyEliminations })
  )
);

const Monitoring = lazy(() =>
  import('pages/period-close/monitoring/Monitoring').then((m) => ({
    default: m.Monitoring,
  }))
);

const Workflows = lazy(() =>
  import('pages/workflows/Workflows').then((m) => ({ default: m.Workflows }))
);

const DataCatalogDemo = lazy(
  () => import('pages/central-data-hub/data-catalog/demo/DataCatalogDemo')
);

const {
  FF__SHOW_WORKFLOWS_MODULE,
  FF__SHOW_ASSETS_MODULE,
  FF__SHOW_FIXED_ASSETS,
  FF__SHOW_AP_AND_ACCRUALS,
  FF__DATA_CATALOG_DEMO,
} = featureFlags;

// Loading fallback component
function UnifiedLedgerPlaceholder() {
  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b">
        <ModuleSectionTitle
          module="Central Data Hub"
          section="Unified Ledger"
        />
      </div>
      <div className="flex items-center justify-center flex-1 text-2xl text-gray-500">
        Under Construction
      </div>
    </div>
  );
}

export function PageLoader() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-2">
      <LoadingAnimation size="large" />
      <span className="text-sm text-gray-500">Loading Page...</span>
    </div>
  );
}

function RedirectComponent() {
  const { userAccessibleModules, modulesLoaded } = useAuthStore();
  const { links } = useMenu(userAccessibleModules);

  // Wait for permissions to load before redirecting
  if (!modulesLoaded) {
    return <PageLoader />;
  }

  // Get the first available path from the menu
  const firstLink = links[0];
  const firstAvailablePath = firstLink?.path ?? firstLink?.children?.[0]?.path;

  if (!firstAvailablePath) {
    return <Navigate to="/404" replace />;
  }

  return <Navigate to={`/${firstAvailablePath}`} replace />;
}

export function PrivateRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const generalRoutes = useGeneralRoutes();

  const { userAccessibleModules, modulesLoaded } = useAuthStore();
  const { links } = useMenu(userAccessibleModules);

  useEffect(() => {
    // If on 404 but user has permissions, navigate to first available page
    // Only do this after modules have loaded to avoid premature redirects
    if (location.pathname === '/404' && modulesLoaded && links.length > 0) {
      const firstAvailablePath =
        links[0]?.path ?? links[0]?.children?.[0]?.path;
      if (firstAvailablePath) {
        navigate(`/${firstAvailablePath}`, { replace: true });
      }
    }
  }, [links, location, modulesLoaded, navigate]);

  const routes = useRoutes([
    {
      path: '/',
      element: <AppLayout />,
      children: [
        ...generalRoutes,
        { element: <RedirectComponent />, index: true },
        { path: 'authenticate', element: <Navigate to="/" replace /> },

        // Revenue
        RevenueRoutes,

        // Cash
        CashRoutes,

        // AP and Accruals
        ...(FF__SHOW_AP_AND_ACCRUALS
          ? [
              {
                path: 'ap-and-accruals',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ApAndAccurals />
                  </Suspense>
                ),
              },
              {
                path: 'ap-and-accurals/bill/:id',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <DemoBillDetails />
                  </Suspense>
                ),
              },
            ]
          : []),

        // Fixed Assets (independent flag)
        ...(FF__SHOW_FIXED_ASSETS
          ? [
              {
                path: 'fixed-assets',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <FixedAssets />
                  </Suspense>
                ),
              },
            ]
          : []),

        // Other Subledgers
        ...(FF__SHOW_ASSETS_MODULE
          ? [
              {
                path: 'prepaid',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Prepaids />
                  </Suspense>
                ),
              },
              {
                path: 'expenses-and-ap',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ExpensesAndAccountsPayable />
                  </Suspense>
                ),
              },
              {
                path: 'expenses-and-ap/bill/:id',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <BillDetails />
                  </Suspense>
                ),
              },
            ]
          : []),

        // Reporting
        {
          path: 'reporting',
          children: [
            {
              index: true,
              element: (
                <Navigate
                  to="/reporting/reports-hub/financial-analysis"
                  replace
                />
              ),
            },
            ...reportsHubRoutes,
            ...consolidationRoutes,
            {
              path: 'intercompany-eliminations',
              element: (
                <PermissionGuard moduleKey="intercompany-eliminations">
                  <Suspense fallback={<PageLoader />}>
                    <IntercompanyEliminations
                      activeTab={0}
                      title="Intercompany Eliminations"
                    />
                  </Suspense>
                </PermissionGuard>
              ),
            },
          ],
        },

        // Period Close
        {
          path: 'monitoring',
          element: (
            <PermissionGuard moduleKey="monitoring">
              <Suspense fallback={<PageLoader />}>
                <Monitoring />
              </Suspense>
            </PermissionGuard>
          ),
        },
        // Checklist v2 & Close routes
        ...CloseRoutes,
        // TODO: Remove legacy redirects for backward compatibility
        {
          path: 'checklist',
          element: <Navigate to="/close/checklist/list" replace />,
        },
        {
          path: 'reconciliations',
          element: <Navigate to="/close/reconciliations" replace />,
        },
        {
          path: 'flux-analysis',
          element: (
            <PermissionGuard moduleKey="flux-analysis">
              <Suspense fallback={<PageLoader />}>
                <FluxAnalysisList />
              </Suspense>
            </PermissionGuard>
          ),
        },
        {
          path: 'flux-analysis/:id',
          element: (
            <PermissionGuard moduleKey="flux-analysis">
              <Suspense fallback={<PageLoader />}>
                <FluxAnalysisDetails />
              </Suspense>
            </PermissionGuard>
          ),
        },

        // Central Data Hub
        {
          path: 'central-data-hub',
          children: [
            {
              path: 'unified-ledger',
              element: <UnifiedLedgerPlaceholder />,
            },
            ...(FF__DATA_CATALOG_DEMO
              ? [
                  {
                    path: 'data-catalog',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <DataCatalogDemo />
                      </Suspense>
                    ),
                  },
                ]
              : []),
            MappingRoutes,
            IntegrationsRoutes,
          ],
        },

        // Workflows
        ...(FF__SHOW_WORKFLOWS_MODULE
          ? [
              {
                path: 'workflows',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Workflows />
                  </Suspense>
                ),
              },
            ]
          : []),

        // Settings
        ...getSettingsRoutes('organization-settings'),

        // Fallback
        {
          path: '*',
          element: (
            <Suspense fallback={<PageLoader />}>
              <NotFound />
            </Suspense>
          ),
        },
      ],
    },
  ]);

  return routes;
}
